// ============================================================================
// 🎯 COMPONENTE - HEADER DO FINANCEIRO — V2 LIGHT+DARK ULTRA PREMIUM
// Stripe / Apple Wallet / Linear aesthetic · Light + Dark mode
// ============================================================================
// 🔥 V2: Soporte completo Light Mode + Dark Mode vía prop isDark
// 🔥 V2: Light: fondos blancos sólidos, bordes grises, sombras suaves
// 🔥 V2: Dark: glassmorphism original intacto (sin cambios)
// 🔥 V2: Título "Financeiro": text-gray-900 en light, text-white en dark
// 🔥 V2: Tooltips, botones, chips, filtros adaptados a ambos modos
// 🔥 V2: MonthPicker con color-scheme dinámico
// ✅ Zero breaking changes — todas as props originais mantidas
// ============================================================================

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign,
  Calendar,
  RefreshCw,
  Plus,
  Download,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";

// ─── helpers ────────────────────────────────────────────────────────────────

const springConfig = { type: "spring", stiffness: 400, damping: 28 };

const fmt = (n) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  }).format(n);

// ─── Skeleton ───────────────────────────────────────────────────────────────

const Skeleton = ({ className = "", isDark = true }) => (
  <div
    className={`rounded-xl overflow-hidden relative ${className}`}
    style={{
      background: isDark ? "rgba(255,255,255,0.05)" : "#e5e7eb",
      isolation: "isolate",
    }}
  >
    <motion.div
      className="absolute inset-0"
      style={{
        background: isDark
          ? "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.07) 50%, transparent 100%)"
          : "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.05) 50%, transparent 100%)",
      }}
      animate={{ x: ["-100%", "100%"] }}
      transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }}
    />
  </div>
);

// ─── Tooltip ────────────────────────────────────────────────────────────────

const Tooltip = ({ label, children, isDark = true }) => {
  const [show, setShow] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute -bottom-9 left-1/2 -translate-x-1/2 z-50 whitespace-nowrap
                       px-2.5 py-1 rounded-lg text-xs font-medium shadow-xl pointer-events-none
                       ${isDark
                         ? "bg-gray-900 text-gray-100 border border-white/10 shadow-black/40"
                         : "bg-white text-gray-700 border border-gray-200 shadow-gray-200/60"
                       }`}
          >
            {label}
            <div className={`absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45
                            border-l border-t
                            ${isDark
                              ? "bg-gray-900 border-white/10"
                              : "bg-white border-gray-200"
                            }`} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── IconButton ─────────────────────────────────────────────────────────────

const IconButton = ({ onClick, disabled, tooltip, children, className = "", isDark = true }) => (
  <Tooltip label={tooltip} isDark={isDark}>
    <motion.button
      onClick={onClick}
      disabled={disabled}
      whileHover={{ scale: 1.06, y: -1 }}
      whileTap={{ scale: 0.94 }}
      transition={springConfig}
      className={`relative flex items-center justify-center w-9 h-9 rounded-xl
                  transition-colors duration-200
                  disabled:opacity-40 disabled:cursor-not-allowed
                  ${isDark
                    ? "border border-white/10 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-gray-100"
                    : "border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                  }
                  ${className}`}
    >
      {children}
    </motion.button>
  </Tooltip>
);

// ─── FilterChip ─────────────────────────────────────────────────────────────

const FILTERS = [
  { value: "todos",    label: "Todos" },
  { value: "receita",  label: "Receitas" },
  { value: "despesa",  label: "Despesas" },
  { value: "vendas",   label: "Vendas" },
  { value: "alugueis", label: "Aluguéis" },
];

const FilterChip = ({ value, label, active, onClick, isDark = true }) => (
  <motion.button
    onClick={() => onClick(value)}
    whileHover={{ y: -1 }}
    whileTap={{ scale: 0.96 }}
    transition={springConfig}
    className={`relative px-3.5 py-1.5 rounded-full text-xs font-semibold
                transition-colors duration-200 select-none whitespace-nowrap
                ${active
                  ? "text-white"
                  : isDark
                    ? "text-gray-400 hover:text-gray-200 bg-white/5 hover:bg-white/8"
                    : "text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200"
                }`}
  >
    {active && (
      <motion.div
        layoutId="chip-bg"
        className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/30"
        transition={springConfig}
      />
    )}
    <span className="relative z-10">{label}</span>
  </motion.button>
);

// ─── BalanceBadge ────────────────────────────────────────────────────────────

const BalanceBadge = ({ saldo, loading, isDark = true }) => {
  const positive = saldo >= 0;
  const zero = saldo === 0;

  const Icon = zero ? Minus : positive ? TrendingUp : TrendingDown;
  
  const colorDark = zero ? "text-gray-400" : positive ? "text-emerald-400" : "text-red-400";
  const colorLight = zero ? "text-gray-600" : positive ? "text-emerald-700" : "text-red-700";
  const color = isDark ? colorDark : colorLight;

  const bgDark = zero
    ? "from-gray-500/10 to-gray-600/10 border-gray-500/20"
    : positive
    ? "from-emerald-500/10 to-teal-500/10 border-emerald-500/20"
    : "from-red-500/10 to-rose-500/10 border-red-500/20";
  const bgLight = zero
    ? "from-gray-50 to-gray-100 border-gray-200"
    : positive
    ? "from-emerald-50 to-teal-50 border-emerald-200"
    : "from-red-50 to-rose-50 border-red-200";
  const bg = isDark ? bgDark : bgLight;

  if (loading) return <Skeleton className="w-36 h-9" isDark={isDark} />;

  return (
    <motion.div
      key={String(positive)}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={springConfig}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-xl
                  bg-gradient-to-r ${bg} border
                  ${isDark ? "backdrop-blur-sm" : ""}`}
    >
      <Icon size={14} className={color} />
      <span className={`text-sm font-bold tabular-nums tracking-tight ${color}`}>
        {fmt(saldo)}
      </span>
    </motion.div>
  );
};

// ─── PulseIcon ───────────────────────────────────────────────────────────────

const PulseIcon = () => (
  <div className="relative flex items-center justify-center w-10 h-10">
    <motion.div
      className="absolute inset-0 rounded-xl bg-emerald-500/20"
      animate={{ scale: [1, 1.35, 1], opacity: [0.6, 0, 0.6] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
    />
    <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600
                    flex items-center justify-center shadow-lg shadow-emerald-500/25">
      <DollarSign size={18} className="text-white" strokeWidth={2.5} />
    </div>
  </div>
);

// ─── MonthPicker ─────────────────────────────────────────────────────────────

const MonthPicker = ({ value, onChange, isMobile, isDark = true }) => (
  <div className="relative">
    <Calendar
      size={14}
      className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10
                  ${isDark ? "text-gray-500" : "text-gray-400"}`}
    />
    <input
      type="month"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Selecionar mês"
      className={`appearance-none rounded-xl text-sm outline-none transition-all duration-200 cursor-pointer
                  ${isMobile ? "pl-8 pr-3 py-2 text-xs w-full" : "pl-8 pr-3 py-2 w-40"}
                  ${isDark
                    ? "border border-white/10 bg-white/5 text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 focus:bg-white/8 hover:bg-white/8 hover:border-white/15 [color-scheme:dark]"
                    : "border border-gray-200 bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 hover:border-gray-300 [color-scheme:light]"
                  }`}
    />
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const FinanceHeader = ({
  company,
  isMobile,
  isDark = true, // 🆕 V2: soporte light/dark (default true = no rompe)
  mes,
  setMes,
  filtroTipo,
  setFiltroTipo,
  onRefresh,
  onNewClick,
  onExportPDF,
  loading = false,
  saldoMes = 0,
}) => {
  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: -8 },
    visible: { opacity: 1, y: 0, transition: springConfig },
  };

  // 🆕 V2: Colores adaptativos
  const titleColor = isDark ? "text-white" : "text-gray-900";
  const companyColor = isDark ? "text-gray-500" : "text-gray-500";
  const dividerColor = isDark ? "bg-white/10" : "bg-gray-200";

  return (
    <motion.header
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="w-full mb-8"
    >
      {/* Glass card */}
      <div
        className="relative rounded-2xl border overflow-hidden"
        style={{
          background: isDark
            ? "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
          backdropFilter: isDark ? "blur(24px)" : "none",
          WebkitBackdropFilter: isDark ? "blur(24px)" : "none",
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          boxShadow: isDark
            ? "0 1px 0 0 rgba(255,255,255,0.06) inset, 0 24px 64px -12px rgba(0,0,0,0.5)"
            : "0 1px 0 0 rgba(255,255,255,0.9) inset, 0 4px 24px -8px rgba(0,0,0,0.04)",
        }}
      >
        {/* Subtle top shimmer */}
        <div className={`absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent ${isDark ? "via-white/12" : "via-black/5"} to-transparent`} />

        <div className={`${isMobile ? "p-4 gap-4" : "px-6 py-5 gap-5"} flex flex-col`}>

          {/* ── Row 1: Logo · Title · Balance · Actions ── */}
          <motion.div
            variants={itemVariants}
            className="flex items-center justify-between gap-4"
          >
            {/* Left: icon + title */}
            <div className="flex items-center gap-3.5 min-w-0">
              <PulseIcon />
              <div className="min-w-0">
                <h1
                  className={`font-extrabold tracking-tight leading-none
                              ${isMobile ? "text-xl" : "text-2xl"}
                              ${titleColor}`}
                  style={{ letterSpacing: "-0.03em" }}
                >
                  Financeiro
                </h1>
                {company?.nome && !isMobile && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`text-xs mt-0.5 truncate ${companyColor}`}
                  >
                    {company.nome}
                  </motion.p>
                )}
              </div>
            </div>

            {/* Right: badge + actions */}
            <div className="flex items-center gap-2.5 shrink-0">
              {!isMobile && <BalanceBadge saldo={saldoMes} loading={loading} isDark={isDark} />}

              <IconButton onClick={onRefresh} disabled={loading} tooltip="Atualizar" isDark={isDark}>
                <motion.div
                  animate={loading ? { rotate: 360 } : { rotate: 0 }}
                  transition={
                    loading
                      ? { duration: 1, repeat: Infinity, ease: "linear" }
                      : { duration: 0 }
                  }
                >
                  <RefreshCw size={15} />
                </motion.div>
              </IconButton>

              <IconButton onClick={onExportPDF} tooltip="Exportar PDF" isDark={isDark}>
                <Download size={15} />
              </IconButton>

              {/* Nova Transação — CTA */}
              <motion.button
                onClick={onNewClick}
                whileHover={{ scale: 1.04, y: -2, boxShadow: "0 12px 32px -8px rgba(16,185,129,0.45)" }}
                whileTap={{ scale: 0.96 }}
                transition={springConfig}
                className={`flex items-center gap-1.5 rounded-xl font-semibold text-white
                            bg-gradient-to-r from-emerald-500 to-teal-500
                            shadow-lg shadow-emerald-500/20
                            transition-shadow
                            ${isMobile ? "px-3 py-2 text-xs" : "px-4 py-2 text-sm"}`}
              >
                <Plus size={isMobile ? 13 : 15} strokeWidth={2.5} />
                <span>{isMobile ? "Nova" : "Nova Transação"}</span>
              </motion.button>
            </div>
          </motion.div>

          {/* ── Row 2: Month picker · Filters ── */}
          <motion.div
            variants={itemVariants}
            className={`flex ${isMobile ? "flex-col gap-3" : "items-center gap-3"}`}
          >
            {/* Month */}
            <MonthPicker value={mes} onChange={setMes} isMobile={isMobile} isDark={isDark} />

            {/* Divider */}
            {!isMobile && (
              <div className={`w-px h-5 ${dividerColor} shrink-0`} />
            )}

            {/* Filter chips */}
            <div className={`flex gap-1.5 ${isMobile ? "overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-none" : "flex-wrap"}`}>
              {FILTERS.map((f) => (
                <FilterChip
                  key={f.value}
                  value={f.value}
                  label={f.label}
                  active={filtroTipo === f.value}
                  onClick={setFiltroTipo}
                  isDark={isDark}
                />
              ))}
            </div>

            {/* Balance badge mobile */}
            {isMobile && (
              <BalanceBadge saldo={saldoMes} loading={loading} isDark={isDark} />
            )}
          </motion.div>

        </div>
      </div>
    </motion.header>
  );
};

export default FinanceHeader;